/**
 * A card is the real asset in a positioned box. No canvas, no drawing: the only
 * things CSS makes are the chrome *around* the face — the trump wash, the ink
 * stamps, the lift and the ring.
 *
 * Illegal cards are muted with a filter on the element and never with opacity:
 * over a coloured felt, opacity tints the face and the cards turn sickly
 * (FINDINGS trap 1). The suit ink survives the filter, so you can still count
 * your trumps while following suit. Trickster dims with a 35 % black scrim on
 * the face instead; that is the grey veil this deck's ink cannot afford, so the
 * filter stays.
 *
 * ## Three layers, because travel and lift are different things
 *
 * Trickster's card is a positioned box whose own transform is *where the card
 * is on the table* and whose inner wrapper's transform is *how far it is raised
 * off the table*, each with its own transition. Splitting them is what lets a
 * card slide to a new place in the fan while it is already lifted, and what
 * stops a deal from fighting a hover. This card does the same:
 *
 *   .card        travel  — placed by the Fan: along the hand, the arc, the tilt
 *     .card-lift lift    — rest / legal / hover / selected, plus the shadow,
 *                          which belongs to whatever is raised
 *       .card-flip       — the two faces of one card, back at 0° and face at
 *                          180°, so a deal can turn it over for real
 *
 * The back is only rendered when someone will actually see it turn (`flip`),
 * because every hand would otherwise carry ten copies of an image that is never
 * on screen.
 */
import { useLayoutEffect, useRef, type CSSProperties, type ReactNode, type SyntheticEvent } from 'react'
import type { CardId } from '@cards/shared'
import { cardAlt, cardSrc, DEFAULT_DECK, type CardArt, type DeckName } from '../cards/assets'
import { throwIn } from './flight'

export interface CardFaceProps {
  card: CardArt
  deck?: DeckName
  /** `legal` lifts, `illegal` mutes, `selected` lifts further with a brass ring,
   *  `waiting` mutes the whole hand while it is somebody else's turn. */
  state?: 'legal' | 'illegal' | 'selected' | 'rest' | 'waiting'
  /** On its way to the table: raised clear of the fan until the server's
   *  answer takes it there. */
  launching?: boolean
  trump?: boolean
  stamp?: string | null
  fromKitty?: boolean
  /** Render the back too, so the card can be turned over. Only the deal needs
   *  it, and only the deal's own keyframes drive the turn — the card's resting
   *  state is always face up. */
  flip?: boolean
  style?: CSSProperties
  className?: string
  onClick?: () => void
  disabled?: boolean
  /** Always a button, even while it has no `onClick`. A card in the hand you
   *  play from goes in and out of being playable — your turn comes, a play is
   *  sent and the hand locks — and a card that changed from a `<div>` to a
   *  `<button>` would be a new element: React would remount it, and a card
   *  being carried or thrown (touch.ts) would be dropped back into the fan. */
  control?: boolean
  children?: ReactNode
  rootRef?: (el: HTMLElement | null) => void
  /** Written as `data-card`, which is how a fan's pointer handling finds the
   *  card under a finger (touch.ts). */
  cardKey?: string
}

export function CardFace({
  card,
  deck = DEFAULT_DECK,
  state = 'rest',
  launching,
  trump,
  stamp,
  fromKitty,
  flip,
  style,
  className,
  onClick,
  disabled,
  control,
  children,
  rootRef,
  cardKey,
}: CardFaceProps): JSX.Element {
  const classes = [
    'card',
    state === 'rest' ? '' : state,
    launching ? 'launch' : '',
    trump ? 'trump' : '',
    flip ? 'flips' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')
  const content = (
    <span className="card-lift">
      <span className="card-flip">
        {flip ? <img className="card-back" src={cardSrc('BACK', deck)} alt="" /> : null}
        <img className="card-front" src={cardSrc(card, deck)} alt={cardAlt(card)} onLoad={faceLoaded} ref={faceRef} />
      </span>
      {stamp ? <span className="bower">{stamp}</span> : null}
      {fromKitty ? <span className="kitty-tag">Kitty</span> : null}
      {className?.split(' ').includes('coach') ? <span className="coach-tag">Coach</span> : null}
      {children}
    </span>
  )
  if (!onClick && !control) {
    return (
      <div className={classes} style={style} ref={rootRef} data-card={cardKey}>
        {content}
      </div>
    )
  }
  // A control with nothing to do is marked disabled for assistive tech and
  // taken out of the tab order, but not given `disabled`: a disabled button
  // gets no pointer events in some browsers, and any card in the hand can
  // still be picked up.
  const idle = !onClick
  return (
    <button
      className={classes}
      style={style}
      onClick={onClick}
      disabled={idle ? undefined : disabled}
      aria-disabled={idle ? true : undefined}
      tabIndex={idle ? -1 : undefined}
      type="button"
      ref={rootRef}
      data-card={cardKey}
    >
      {content}
    </button>
  )
}

/** A card thrown into the trick: per-seat rotation, and the brass ring when it
 *  is the one currently taking the trick.
 *
 *  `tilt` is the seat's own slant for this particular card. Trickster draws a
 *  fresh rotation inside a per-seat range every time a card is played and then
 *  pins it for the life of the card, so no two tricks stack the same way but a
 *  resize never re-scatters the one on the table. Ours is drawn from the card
 *  itself rather than from `Math.random`, so the scatter also survives a
 *  reconnect — and every player at the table sees the same table. */
export function TrickCard({
  card,
  position,
  winning,
  tilt,
  fresh,
  seat,
  deck = DEFAULT_DECK,
}: {
  card: CardId
  position: 'n' | 'e' | 's' | 'w'
  /** Who played it: how a gesture from another player names this card
   *  (shared/protocol/gesture.ts), written as `data-seat`. */
  seat?: number
  winning?: boolean
  tilt?: number
  /** Played in this very snapshot, so it is thrown in from the hand that
   *  played it. A card that was already on the table when this one mounted —
   *  a reconnect, a screen coming back — is simply there. */
  fresh?: boolean
  deck?: DeckName
}): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null)
  // Layout, not a plain effect: the card has to be moved back onto its origin
  // before the first frame in which it is on the table, or that frame shows it
  // already landed and the flight starts with a jump.
  useLayoutEffect(() => {
    if (fresh && ref.current) throwIn(ref.current, card, position, tilt ?? 0)
    // Mount only: a flight is the arrival, and a card arrives once.
  }, [])
  return (
    <div
      ref={ref}
      className={`tcard ${position}${winning ? ' winning' : ''}`}
      data-seat={seat}
      style={tilt === undefined ? undefined : ({ '--tilt': `${tilt}deg` } as CSSProperties)}
    >
      <img src={cardSrc(card, deck)} alt={cardAlt(card)} onLoad={faceLoaded} ref={faceRef} />
    </div>
  )
}

/** The card is showing its face: the picture has arrived. Anything laid over
 *  the face — the tilt glint — waits for this, or it plays over an empty
 *  card while the image is still loading. An attribute React does not
 *  manage, on whichever card the picture belongs to. */
function markFace(img: HTMLImageElement): void {
  img.closest<HTMLElement>('.card, .tcard')?.setAttribute('data-face', '')
}
function faceLoaded(e: SyntheticEvent<HTMLImageElement>): void {
  markFace(e.currentTarget)
}
/** A picture already in the cache can be complete before React listens. */
function faceRef(img: HTMLImageElement | null): void {
  if (img?.complete && img.naturalWidth > 0) markFace(img)
}

/** Small face-up card used for an exposed hand (Open Misère) and for records. */
export function MiniCard({ card, deck = DEFAULT_DECK }: { card: CardArt; deck?: DeckName }): JSX.Element {
  return (
    <div className="mini">
      <img src={cardSrc(card, deck)} alt={cardAlt(card)} />
    </div>
  )
}

/** The Misère partner sitting out, squared up — never a fan of backs, which
 *  reads as a hand still in play. That is the wire's own rule for the
 *  `faceDown` holding and not this component's idea; `SeatFan` below renders
 *  the other one, `count`, which *is* a hand still in play. Two cards, because
 *  this is deliberately not a count of anything. */
export function FaceDownStack({ count, deck = DEFAULT_DECK }: { count: number; deck?: DeckName }): JSX.Element {
  return (
    <div className="stack">
      {Array.from({ length: Math.min(count, 2) }, (_, i) => (
        <img key={i} src={cardSrc('BACK', deck)} alt="" />
      ))}
    </div>
  )
}

/** The most cards a seat fans. Ten is a 500 hand and thirteen a Bridge one;
 *  past that the backs stop being countable anyway and the number under the
 *  name is doing the work. */
const SEAT_FAN_MAX = 13

/**
 * What another player is holding: their real card count, fanned face down and
 * turned to face their side of the table.
 *
 * This is the thing that makes the felt read as a table rather than as three
 * captions and a trick — before it, a `count` holding drew nothing whatever, so
 * the only hand on the table was your own. Trickster draws it two ways — every other seat's cards
 * are real card elements on a desktop, and on a phone (where thirty extra cards
 * is thirty extra cards) `_redrawFan` swaps them for one small fanned graphic
 * per seat. We are a phone table, so we take the second: a token hand, sized
 * off `--card-w` so it follows the card-size setting, and never interactive.
 *
 * It also carries the turn, the way theirs does: `getFan(n, dimmed)` is passed
 * `currentPlayer && currentPlayer !== player`, so the seat about to play is the
 * one holding bright cards. Here that is `lit`, and it doubles the brass on the
 * name rather than replacing it — a seat you are waiting on should be findable
 * without reading.
 *
 * Geometry lives in CSS (`--i`, `--n` are the only inputs) because every length
 * it needs is already a card token, and a second fan measured in JavaScript is
 * a second fan to keep in step with the first.
 */
export function SeatFan({
  count,
  position,
  lit,
  deck = DEFAULT_DECK,
}: {
  count: number
  position: 'n' | 'e' | 's' | 'w'
  /** It is this seat's turn. */
  lit?: boolean
  deck?: DeckName
}): JSX.Element {
  const n = Math.min(count, SEAT_FAN_MAX)
  return (
    <div
      className={`seat-fan seat-fan-${position}${lit ? ' lit' : ''}`}
      style={{ '--n': n } as CSSProperties}
      // One label for the hand, not twelve alt texts for twelve identical backs.
      role="img"
      aria-label={`${count} card${count === 1 ? '' : 's'}`}
    >
      <div className="seat-fan-cards">
        {Array.from({ length: n }, (_, i) => (
          <img key={i} src={cardSrc('BACK', deck)} alt="" style={{ '--i': i } as CSSProperties} />
        ))}
      </div>
    </div>
  )
}
